import React, { useEffect, useState } from "react";
import { fetchApplicationBlockchain } from "../ApplicationDetail.helpers";
import applicationDetailSignal from "../ApplicationDetail.signal";
import { Alert, Button } from "react-bootstrap";
import ReactDiffViewer from "react-diff-viewer";
import { enumCaseToTabCase } from "../../../../libs/string";

const MODEL_CHANGE_ACTIONS = [
  'DISBURSEMENT_CREATED',
  'DISBURSEMENT_UPDATED',
  'DISBURSEMENT_DELETED',
  'TRANSACTION_CREATED',
  'TRANSACTION_UPDATED',
  'TRANSACTION_DELETED',
  'LOAN_TRACKING_CREATED',
  'LOAN_TRACKING_UPDATED',
  'LOAN_TRACKING_DELETED',
  'CLAIM_TRACKING_CREATED',
  'CLAIM_TRACKING_UPDATED',
  'CLAIM_TRACKING_DELETED',
];

/** Full application snapshots make note-only chain blocks unreadable in the diff viewer. */
function sliceStateForNoteDiff(appState: object | undefined) {
  if (!appState || typeof appState !== 'object') {
    return { updatedDate: undefined, notes: [] as unknown[] };
  }
  const o = appState as Record<string, unknown>;
  const notes = o.notes;
  return {
    applicationId: o.id,
    updatedDate: o.updatedDate,
    notes: Array.isArray(notes) ? notes : [],
  };
}

/** LOAD_OCR_RESULT chain blocks only add/update raw OCR; full snapshots swamp the diff viewer. */
function sliceStateForOcrLoadDiff(appState: object | undefined) {
  if (!appState || typeof appState !== 'object') {
    return { updatedDate: undefined, rawOcr: null };
  }
  const o = appState as Record<string, unknown>;
  return {
    applicationId: o.id,
    updatedDate: o.updatedDate,
    rawOcr: o.rawOcr ?? null,
  };
}

/** UPLOADED_DOCUMENTS chain blocks store a full snapshot but only documents changed. */
function sliceStateForDocumentsDiff(appState: object | undefined) {
  if (!appState || typeof appState !== 'object') {
    return { updatedDate: undefined, supportingDocuments: [] as unknown[] };
  }
  const o = appState as Record<string, unknown>;
  const docs = o.supportingDocuments;
  return {
    applicationId: o.id,
    updatedDate: o.updatedDate,
    supportingDocuments: Array.isArray(docs) ? docs : [],
  };
}

function getDiffValuesForBlock(
  chainblock: { action: string; state: string | object },
  prevState: object | undefined,
  isFirstBlock: boolean
) {
  const stateObj =
    typeof chainblock.state === 'string' ? JSON.parse(chainblock.state) : chainblock.state;
  const state = stateObj as Record<string, unknown>;

  if (chainblock.action === 'UPDATED_APPLICATION_DETAILS' && state?.before != null && state?.after != null) {
    return {
      oldValue: state.before as object,
      newValue: state.after as object,
    };
  }

  if (chainblock.action === 'ADDED_NOTE' || chainblock.action === 'UPDATED_NOTE') {
    return {
      oldValue: isFirstBlock ? {} : sliceStateForNoteDiff(prevState),
      newValue: sliceStateForNoteDiff(stateObj as object),
    };
  }

  if (chainblock.action === 'LOAD_OCR_RESULT') {
    return {
      oldValue: isFirstBlock ? {} : sliceStateForOcrLoadDiff(prevState),
      newValue: sliceStateForOcrLoadDiff(stateObj as object),
    };
  }

  if (chainblock.action === 'UPLOADED_DOCUMENTS' || chainblock.action === 'UPLOADED_DOCUMENT') {
    return {
      oldValue: isFirstBlock ? {} : sliceStateForDocumentsDiff(prevState),
      newValue: sliceStateForDocumentsDiff(stateObj as object),
    };
  }

  if (!MODEL_CHANGE_ACTIONS.includes(chainblock.action)) {
    return {
      oldValue: isFirstBlock ? {} : prevState,
      newValue: stateObj,
    };
  }
  const eventCreatedActions = [
    'DISBURSEMENT_CREATED', 'TRANSACTION_CREATED',
    'LOAN_TRACKING_CREATED', 'CLAIM_TRACKING_CREATED',
  ];
  const eventUpdatedActions = [
    'DISBURSEMENT_UPDATED', 'TRANSACTION_UPDATED',
    'LOAN_TRACKING_UPDATED', 'CLAIM_TRACKING_UPDATED',
  ];
  const eventDeletedActions = [
    'DISBURSEMENT_DELETED', 'TRANSACTION_DELETED',
    'LOAN_TRACKING_DELETED', 'CLAIM_TRACKING_DELETED',
  ];
  if (eventCreatedActions.includes(chainblock.action)) {
    return { oldValue: {}, newValue: state };
  }
  if (eventUpdatedActions.includes(chainblock.action)) {
    const payload = state as { before?: object; after?: object };
    return {
      oldValue: payload.before ?? {},
      newValue: payload.after ?? state,
    };
  }
  if (eventDeletedActions.includes(chainblock.action)) {
    return { oldValue: state, newValue: {} };
  }
  return { oldValue: prevState ?? {}, newValue: state };
}

const ApplicationBlockchainTab = () => {
  const { id, blockchain, updatedDate } = applicationDetailSignal.value;
  const [visibleIndex, setVisibleIndex] = useState<number>();

  useEffect(() => {
    if (id) {
      fetchApplicationBlockchain();
    }
  }, [id, updatedDate]);

  if (!blockchain || blockchain.length === 0) {
    return (
      <Alert variant="info" className="mb-0">
        <p className="mb-0">
          No blockchain entries have been recorded for this application.
        </p>
      </Alert>
    );
  }

  return (
    <div className="p-8">
      {blockchain.map((chainblock, index) => {
        const firstBlock = index === 0;
        const prevStateRaw = firstBlock ? undefined : blockchain[index - 1].state;
        const prevState =
          prevStateRaw === undefined
            ? undefined
            : typeof prevStateRaw === 'string'
              ? JSON.parse(prevStateRaw)
              : prevStateRaw;
        const { oldValue, newValue } = getDiffValuesForBlock(
          chainblock,
          prevState,
          firstBlock
        );
        return (
          <React.Fragment key={chainblock.id}>
            <div className="border-secondary mb-32">
              <p>
                <b>ID:</b> {chainblock.id}
              </p>
              <p>
                <b>Previous block ID:</b> {chainblock.previousBlockId || '-'}
              </p>
              <p>
                <b>Action:</b>{' '}
                {enumCaseToTabCase(chainblock.action).replace('Ocr', 'OCR')}
              </p>
              <p>
                <b>Timestamp:</b> {chainblock.createdDate}
              </p>
              <p>
                <b>HMAC Signature:</b> {chainblock.hmacTag}
              </p>
              <p>
                <b>User:</b> {chainblock.user.firstName}{' '}
                {chainblock.user.lastName} - {chainblock.user.email}
              </p>
              <div className="text-right">
                <Button
                  variant="outline-primary"
                  className="rounded"
                  size="sm"
                  onClick={() => {
                    if (visibleIndex === index) {
                      return setVisibleIndex(undefined);
                    }

                    setVisibleIndex(index);
                  }}
                >
                  {visibleIndex === index ? 'Hide diff' : 'Show diff'}
                </Button>
              </div>
            </div>
            {visibleIndex === index && (
              <ReactDiffViewer
                oldValue={JSON.stringify(oldValue, null, 2)}
                newValue={JSON.stringify(newValue, null, 2)}
                splitView={true}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ApplicationBlockchainTab;
